import { createExtension } from "../../api/extension";
import {
  createRingGroup,
  createRingGroupDestination,
} from "../../api/ringgroup";
import { createIvrMenu, createIvrOption } from "../../api/ivr";
import { parseError } from "@ntconnect/framework/pbx";
import Logger from "../../common/utils/Logger";

/**
 * Execute an AI-proposed PBX plan: extensions first, then ring groups
 * (with destinations), then IVR menus (with options). Each item streams a
 * progress event so the wizard UI can render a live checklist instead of
 * a single opaque spinner.
 *
 * onProgress signature: ({ kind, label, status, error }) where
 *   kind   ∈ "extension" | "ringgroup" | "ringgroupDest" | "ivr" | "ivrOption"
 *   status ∈ "pending" | "ok" | "fail"
 */

function randomPassword(len = 14) {
  const alpha =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < len; i++) {
    out += alpha[Math.floor(Math.random() * alpha.length)];
  }
  return out;
}

export async function applyPlan({ domain, plan, onProgress }) {
  const emit = (evt) => onProgress?.(evt);
  const generatedPasswords = {};
  const results = {
    extensions: [],
    ringGroups: [],
    ivrs: [],
    failures: [],
  };

  // Extensions first — ring groups + IVR destinations reference them.
  for (const e of plan.extensions || []) {
    const password = randomPassword();
    generatedPasswords[e.extension] = password;

    emit({ kind: "extension", label: `Extension ${e.extension} · ${e.name}`, status: "pending" });

    const res = await createExtension(domain, {
      extension: e.extension,
      password,
      name: e.name,
      description: e.description || "",
      tollAllow: false,
    });
    Logger("applyPlan · createExtension", res);

    if (res?.ok) {
      results.extensions.push(e);
      emit({ kind: "extension", label: `Extension ${e.extension} · ${e.name}`, status: "ok" });
    } else {
      const error = parseError(res);
      results.failures.push({ kind: "extension", item: e.extension, error });
      emit({ kind: "extension", label: `Extension ${e.extension} · ${e.name}`, status: "fail", error });
    }
  }

  // Ring groups + their destinations.
  for (const g of plan.ringGroups || []) {
    emit({ kind: "ringgroup", label: `Ring group ${g.extension} · ${g.name}`, status: "pending" });

    const res = await createRingGroup(domain, {
      ring_group_extension: g.extension,
      ring_group_name: g.name,
      ring_group_call_timeout: "30",
      ring_group_strategy: "enterprise",
      ring_group_timeout_action: "",
    });
    Logger("applyPlan · createRingGroup", res);

    if (!res?.ok) {
      const error = parseError(res);
      results.failures.push({ kind: "ringgroup", item: g.extension, error });
      emit({ kind: "ringgroup", label: `Ring group ${g.extension} · ${g.name}`, status: "fail", error });
      continue;
    }
    results.ringGroups.push(g);
    emit({ kind: "ringgroup", label: `Ring group ${g.extension} · ${g.name}`, status: "ok" });

    const ringGroupKey = res.data?.ring_group_extension || g.extension;
    for (const memberExt of g.memberExtensions || []) {
      emit({ kind: "ringgroupDest", label: `↳ ${memberExt}`, status: "pending" });
      const dres = await createRingGroupDestination(domain, ringGroupKey, {
        destination_number: memberExt,
        destination_delay: "0",
        destination_timeout: "30",
        destination_enabled: true,
      });
      Logger("applyPlan · createRingGroupDestination", dres);
      if (dres?.ok) {
        emit({ kind: "ringgroupDest", label: `↳ ${memberExt}`, status: "ok" });
      } else {
        const error = parseError(dres);
        results.failures.push({
          kind: "ringgroupDest",
          item: `${g.extension}/${memberExt}`,
          error,
        });
        emit({ kind: "ringgroupDest", label: `↳ ${memberExt}`, status: "fail", error });
      }
    }
  }

  // IVR menus + their options.
  for (const ivr of plan.ivrs || []) {
    emit({ kind: "ivr", label: `IVR ${ivr.extension} · ${ivr.name}`, status: "pending" });

    const res = await createIvrMenu(domain, {
      ivr_menu_name: ivr.name,
      ivr_menu_extension: ivr.extension,
      ivr_menu_description: undefined,
      ivr_menu_greet_long: ivr.greeting || undefined,
      ivr_menu_timeout: 10,
    });
    Logger("applyPlan · createIvrMenu", res);

    if (!res?.ok) {
      const error = parseError(res);
      results.failures.push({ kind: "ivr", item: ivr.extension, error });
      emit({ kind: "ivr", label: `IVR ${ivr.extension} · ${ivr.name}`, status: "fail", error });
      continue;
    }
    results.ivrs.push(ivr);
    emit({ kind: "ivr", label: `IVR ${ivr.extension} · ${ivr.name}`, status: "ok" });

    const menuId =
      res.data?.id || res.data?.ivr_menu_uuid || res.data?.uuid;
    if (!menuId) {
      results.failures.push({
        kind: "ivr",
        item: ivr.extension,
        error: "Menu created but no id returned — options skipped.",
      });
      continue;
    }

    for (let i = 0; i < (ivr.options || []).length; i++) {
      const opt = ivr.options[i];
      emit({
        kind: "ivrOption",
        label: `↳ Press ${opt.digit} → ${opt.label}`,
        status: "pending",
      });
      const ores = await createIvrOption(domain, menuId, {
        ivr_menu_option_digits: opt.digit,
        ivr_menu_option_description: opt.label || undefined,
        action: { name: "transfer", value: opt.destinationExtension },
        ivr_menu_option_order: i + 1,
      });
      Logger("applyPlan · createIvrOption", ores);
      if (ores?.ok) {
        emit({
          kind: "ivrOption",
          label: `↳ Press ${opt.digit} → ${opt.label}`,
          status: "ok",
        });
      } else {
        const error = parseError(ores);
        results.failures.push({
          kind: "ivrOption",
          item: `${ivr.extension}/${opt.digit}`,
          error,
        });
        emit({
          kind: "ivrOption",
          label: `↳ Press ${opt.digit} → ${opt.label}`,
          status: "fail",
          error,
        });
      }
    }
  }

  return { ...results, generatedPasswords };
}
